import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiKey, FiMail, FiRefreshCw, FiShield, FiX } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/useI18n';
import { emailLogin, getEmailLoginCaptcha, requestEmailLoginCode } from '../utils/api';

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME || 'PolyOneAI_bot';

/**
 * Telegram 图标组件
 */
function TelegramIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-6 h-6"
    >
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.248-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.14.121.098.154.23.17.324.015.094.035.308.02.476z" />
    </svg>
  );
}

/**
 * 生成 Telegram 登录链接
 * @param {string} apiVersion - API 版本
 * @param {string} language - 用户语言
 * @returns {string} Telegram 登录链接
 */
function generateTelegramLoginUrl(apiVersion, language = 'zh') {
  // 渠道标志：i_渠道base64_l_语言_t_版本
  // 'telegram' 的 base64 编码是 'dGVsZWdyYW0='
  const channelBase64 = 'dGVsZWdyYW0'; // 'telegram' 的 base64（去掉 padding）
  const versionParam = apiVersion ? `t_${apiVersion}` : '';
  const params = `i_${channelBase64}_l_${language}${versionParam ? `_${versionParam}` : ''}`;

  return `https://t.me/${BOT_USERNAME}?start=${params}`;
}

/**
 * 登录弹窗组件
 */
export function LoginModal({ open, onClose }) {
  const { apiVersion, login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [loginMode, setLoginMode] = useState('telegram');
  const [email, setEmail] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [emailMessage, setEmailMessage] = useState('');

  const telegramLoginUrl = generateTelegramLoginUrl(apiVersion, lang);
  const canSend = useMemo(() => !sending && cooldown === 0, [sending, cooldown]);

  const loadCaptcha = useCallback(async () => {
    try {
      setLoadingCaptcha(true);
      const res = await getEmailLoginCaptcha();
      if (res?.code === 0) {
        setCaptchaId(res.data?.captcha_id || '');
        setCaptchaImage(res.data?.image || '');
      } else {
        setEmailMessage(res?.msg || t('loginModal.emailCaptchaFailed'));
      }
    } catch (error) {
      setEmailMessage(error.message || t('loginModal.emailCaptchaFailed'));
    } finally {
      setLoadingCaptcha(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open) return;
    setLoginMode('telegram');
    setEmail('');
    setCaptchaId('');
    setCaptchaImage('');
    setCaptchaCode('');
    setEmailCode('');
    setCooldown(0);
    setEmailMessage('');
  }, [open]);

  useEffect(() => {
    if (!open || loginMode !== 'email') return;
    if (!captchaImage) {
      loadCaptcha();
    }
  }, [open, loginMode, captchaImage, loadCaptcha]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const handleSendCode = async () => {
    if (!email) {
      setEmailMessage(t('loginModal.emailRequired'));
      return;
    }
    if (!captchaCode) {
      setEmailMessage(t('loginModal.emailCaptchaRequired'));
      return;
    }
    try {
      setSending(true);
      setEmailMessage('');
      const res = await requestEmailLoginCode({
        email,
        captchaId,
        captchaCode,
        lang
      });
      if (res?.code === 0) {
        setEmailMessage(t('loginModal.emailCodeSent'));
        setCooldown(60);
      } else {
        setEmailMessage(res?.msg || t('loginModal.emailSendFailed'));
      }
    } catch (error) {
      setEmailMessage(error.message || t('loginModal.emailSendFailed'));
    } finally {
      setSending(false);
      loadCaptcha();
      setCaptchaCode('');
    }
  };

  const handleEmailLogin = async () => {
    if (!emailCode) {
      setEmailMessage(t('loginModal.emailCodeRequired'));
      return;
    }
    try {
      setVerifying(true);
      setEmailMessage('');
      const res = await emailLogin({ email, code: emailCode, lang });
      if (res?.code === 0 && res?.data) {
        login({
          data: res.data,
          user_id: res.data?.user_id
        });
        if (res?.data?.lang) {
          setLang(res.data.lang);
        }
        onClose?.();
        return;
      }
      setEmailMessage(res?.msg || t('loginModal.emailLoginFailed'));
    } catch (error) {
      setEmailMessage(error.message || t('loginModal.emailLoginFailed'));
    } finally {
      setVerifying(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-950/75 backdrop-blur-[18px] flex items-center justify-center p-6 z-[100]"
      role="dialog"
      aria-modal="true"
    >
      <div className="glass-xl w-full max-w-[480px] p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="uppercase tracking-[0.3em] text-[0.6rem] md:text-[0.65rem] text-text-muted">
              {t('loginModal.kicker')}
            </p>
            <h2 className="text-xl md:text-2xl mt-1">{t('loginModal.title', { brand: 'PolyOne' })}</h2>
          </div>
          <button
            className="ghost-pill w-9 h-9 inline-flex items-center justify-center bg-slate-900/60 border border-slate-600/60 text-text rounded-3xl cursor-pointer transition-all duration-200 hover:border-accent hover:text-sky-100"
            onClick={onClose}
            aria-label={t('loginModal.closeAria')}
          >
            <FiX />
          </button>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2 p-1 rounded-full bg-slate-900/60 border border-slate-700/50">
            <button
              onClick={() => setLoginMode('telegram')}
              className={`flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                loginMode === 'telegram'
                  ? 'bg-sky-500/20 text-sky-200'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t('loginModal.telegramTab')}
            </button>
            <button
              onClick={() => setLoginMode('email')}
              className={`flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                loginMode === 'email'
                  ? 'bg-indigo-500/20 text-indigo-200'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t('loginModal.emailTab')}
            </button>
          </div>

          {loginMode === 'telegram' ? (
            <>
              <p className="text-slate-300/70 text-sm md:text-[0.95rem] leading-relaxed">
                {t('loginModal.desc')}
              </p>

              <a
                href={telegramLoginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full px-6 py-4 rounded-2xl bg-[#0088cc] hover:bg-[#0088cc]/90 text-white font-semibold text-base transition-all duration-200 cursor-pointer border-none shadow-lg hover:shadow-xl"
              >
                <TelegramIcon />
                <span>{t('loginModal.button')}</span>
              </a>

              <div className="text-xs text-slate-400 text-center leading-relaxed">
                {t('loginModal.hint').split('\n')[0]}<br />
                {t('loginModal.hint').split('\n')[1]}
              </div>

              <button
                onClick={() => setLoginMode('email')}
                className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
              >
                {t('loginModal.switchToEmail')}
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="text-xs font-medium text-slate-400 uppercase tracking-widest">
                {t('loginModal.emailTitle')}
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  <FiMail className="w-3.5 h-3.5 text-indigo-400" />
                  {t('loginModal.emailLabel')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('loginModal.emailPlaceholder')}
                  className="w-full bg-slate-900/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  <FiShield className="w-3.5 h-3.5 text-indigo-400" />
                  {t('loginModal.emailCaptchaLabel')}
                </label>
                <div className="flex gap-3">
                  <input
                    value={captchaCode}
                    onChange={(e) => setCaptchaCode(e.target.value)}
                    placeholder={t('loginModal.emailCaptchaPlaceholder')}
                    className="flex-1 bg-slate-900/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all"
                  />
                  <button
                    type="button"
                    onClick={loadCaptcha}
                    disabled={loadingCaptcha}
                    className="shrink-0 w-28 h-12 rounded-xl bg-slate-800 border border-slate-700/60 flex items-center justify-center overflow-hidden hover:border-slate-600 transition-colors"
                    title={t('loginModal.emailCaptchaRefresh')}
                  >
                    {loadingCaptcha ? (
                      <FiRefreshCw className="w-4 h-4 text-slate-500 animate-spin" />
                    ) : (
                      captchaImage && (
                        <img src={captchaImage} alt="captcha" className="w-full h-full object-contain" />
                      )
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
                  <FiKey className="w-3.5 h-3.5 text-indigo-400" />
                  {t('loginModal.emailCodeLabel')}
                </label>
                <div className="flex gap-3">
                  <input
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value)}
                    placeholder={t('loginModal.emailCodePlaceholder')}
                    className="flex-1 bg-slate-900/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all"
                  />
                  <button
                    onClick={handleSendCode}
                    disabled={!canSend}
                    className={`
                      shrink-0 px-4 h-12 rounded-xl text-sm font-medium transition-all
                      ${cooldown > 0
                        ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white hover:from-indigo-400 hover:to-sky-400 shadow-lg shadow-indigo-500/20'
                      }
                    `}
                  >
                    {cooldown > 0 ? `${cooldown}s` : t('loginModal.emailSendCode')}
                  </button>
                </div>
              </div>

              {emailMessage ? (
                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  {emailMessage}
                </div>
              ) : null}

              <button
                onClick={handleEmailLogin}
                disabled={verifying}
                className="w-full px-4 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-sky-500 hover:from-indigo-400 hover:to-sky-400 shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {verifying ? (
                  <>
                    <FiRefreshCw className="w-4 h-4 animate-spin" />
                    {t('loginModal.emailLogging')}
                  </>
                ) : (
                  t('loginModal.emailLoginAction')
                )}
              </button>

              <button
                onClick={() => setLoginMode('telegram')}
                className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
              >
                {t('loginModal.switchToTelegram')}
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-slate-700/50">
          <p className="text-xs text-slate-500 leading-relaxed">
            {t('loginModal.terms').split('\n')[0]}<br />
            {t('loginModal.terms').split('\n')[1]}
          </p>
        </div>
      </div>
    </div>
  );
}
